import { z } from "zod";
import { listOffsetSchema } from "./leads.js";
// Rapporto dell'importazione da Close (piano M2, Task 15): ciò che la pagina «Importazione da Close» delle Impostazioni
// legge. Le esecuzioni le avviano il comando `close-import` e, dal 18/10/2026, la sezione «Ogni sera da Close» (in fondo al
// file); il rapporto le mostra e basta, leggendo il riepilogo
// che ogni esecuzione ha salvato (mai ricalcolato) e le sue anomalie. Nel rapporto ci sono soltanto conteggi, valori di
// campi a scelta, nomi delle persone del team e degli utenti di Close e identificativi tecnici di Close: mai testo libero,
// mai nomi o recapiti dei clienti. Le date sono stringhe ISO.
const countSchema = z.number().int().nonnegative();
/** Un'esecuzione nell'elenco, dalla più recente. */
export const importRunRowSchema = z.object({
    id: z.string(),
    mode: z.enum(["dry_run", "apply"]),
    status: z.enum(["running", "completed", "failed", "aborted"]),
    phase: z.enum(["extract", "plan", "apply", "done"]),
    sourceKind: z.enum(["http", "synthetic"]),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    heartbeatAt: z.string(),
    /**
     * Il codice dell'errore di un'esecuzione fermata o chiusa (`close_auth_failed`, `abandoned`…), la prima parola di `import_runs.error`:
     * mai il messaggio, che è tecnico. Assente o nullo senza errore.
     */
    errorCode: z.string().nullable().optional(),
});
/** `GET /api/imports/close/runs`: le esecuzioni dalla più recente, al massimo `IMPORT_RUNS_MAX`. */
export const IMPORT_RUNS_MAX = 100;
export const importRunListSchema = z.object({ runs: z.array(importRunRowSchema) });
/**
 * Un utente di Close e la persona del team a cui l'importazione lo ha abbinato (per email, a mano o nessuna). Owner diventa
 * un'appartenenza di livello 1 attiva o sospesa (INGR-15). `status` è lo stato dell'appartenenza abbinata; `fromClose` dice
 * che è una persona nata sospesa da un'importazione da Close; `outsider`, per chi non è abbinato, che cosa gli succede:
 * `suspended` entra (nella prova a secco: entrerebbe) come persona sospesa, `no_email`, `platform` e `no_role` (l'azienda non
 * ha il ruolo User) restano autori storici,
 * `invited` aspetta che accetti l'invito. Un riepilogo salvato prima li legge con i valori di partenza.
 */
export const IMPORT_USER_OUTSIDERS = ["suspended", "no_email", "invited", "platform", "no_role"];
export const importUserLinkSchema = z.object({
    closeUserId: z.string(),
    closeName: z.string(),
    membershipId: z.string().nullable(),
    membershipDisplayName: z.string().nullable(),
    active: z.boolean(),
    level: z.number().int().nullable(),
    decidedBy: z.enum(["email", "manual", "none"]),
    status: z.enum(["active", "suspended", "archived"]).nullable().default(null),
    fromClose: z.boolean().default(false),
    outsider: z.enum(IMPORT_USER_OUTSIDERS).nullable().default(null),
    /** La persona a cui vanno i Lead di cui l'utente è owner in Close, per una regola del profilo (22/10/2026); di solito nulla. */
    leadsTo: z.object({ membershipId: z.string(), displayName: z.string() }).nullable().default(null),
});
/**
 * Un valore di una variabile a scelta, con quanti Lead lo hanno e la voce in cui entra (`null`: nessuna, in rosso):
 * l'identificativo della voce (`mappedTo`, per un tag il suo nome) e il nome che la voce aveva al momento del piano
 * (`mappedToLabel`, per esempio «30–40 €»), da mostrare così com'è. Un riepilogo salvato prima dei nomi li ha `null`.
 */
export const importVariableValueSchema = z.object({ value: z.string(), leads: countSchema, mappedTo: z.string().nullable(), mappedToLabel: z.string().nullable().default(null) });
/**
 * Una variabile di Close: dove va in Gioia365 (`field:<chiave>`, `system:<campo>`, `note`, `ignored`) e quanti Lead
 * la hanno piena. Soltanto le variabili a scelta mostrano i valori; quelle di testo libero soltanto il conteggio.
 * `destinationLabel` è il nome del campo dell'azienda al momento del piano (per esempio «Fiche media»), da mostrare così
 * com'è; è nullo per un campo di sistema, una nota o una variabile ignorata, che l'interfaccia nomina con le sue
 * traduzioni, e in un riepilogo salvato prima dei nomi.
 */
export const importVariableSchema = z.object({
    closeLabel: z.string(),
    destination: z.string(),
    destinationLabel: z.string().nullable().default(null),
    kind: z.enum(["choice", "free_text", "ignored"]),
    values: z.array(importVariableValueSchema).optional(),
    filled: countSchema,
});
/**
 * Il riepilogo che un'esecuzione ha salvato alla chiusura (`ImportSummary` dell'importatore): nella prova a secco ciò
 * che l'importazione farebbe, nell'importazione vera ciò che ha fatto. `anomalies` conta i casi per codice
 * (`author_not_linked` le attività, anche se le anomalie sono una riga per utente di Close); `notImported` le attività
 * di Close che non si importano, per tipo.
 */
export const importSummarySchema = z.object({
    leads: z.object({ total: countSchema, create: countSchema, update: countSchema, unchanged: countSchema, skipped: countSchema }),
    calls: z.object({ create: countSchema, withRecording: countSchema, invalid: countSchema }),
    notes: z.object({ create: countSchema }),
    // Dal giro di correzioni del Task 14: i cambi di status di Close e gli altri eventi storici, contati a parte. Con un
    // valore di partenza, così un riepilogo salvato prima si legge ancora.
    // `byType`, dal 22/10/2026: fra gli eventi storici, le email di Close (`close.email`) e gli aggiornamenti e le unioni fatti in
    // Close (`close.import_update`, `close.lead_merge`), per tipo.
    events: z.object({ create: countSchema, statusChanges: countSchema.default(0), history: countSchema.default(0), byType: z.record(z.string(), countSchema).default({}) }),
    // Le opportunità di Close entrano dal 21/10/2026 (INGR-26): `opportunities` dice quante sono nate, aggiornate, invariate e
    // rimaste fuori (in una nota, o con un Lead che non si importa). Un riepilogo salvato prima ha soltanto
    // `staged.opportunities`, quelle estratte e rimandate; uno nuovo non lo scrive più.
    opportunities: z
        .object({ create: countSchema, update: countSchema, unchanged: countSchema, skipped: countSchema })
        .default({ create: 0, update: 0, unchanged: 0, skipped: 0 }),
    // I Task aperti di Close entrano dal 22/10/2026 come richiami: `tasks` dice quanti sono nati, aggiornati da Close, chiusi perché
    // completati in Close, invariati e rimasti fuori (in una nota, o con un Lead che non si importa o non si aggiorna). Un riepilogo
    // salvato prima ha soltanto `staged.tasks`, i Task aperti estratti e rimandati; uno nuovo non lo scrive più. `tooOld`: dei rimasti
    // fuori, quelli in nota perché scaduti da più di 30 giorni.
    tasks: z
        .object({ create: countSchema, update: countSchema, completed: countSchema, unchanged: countSchema, skipped: countSchema, tooOld: countSchema.default(0) })
        .default({ create: 0, update: 0, completed: 0, unchanged: 0, skipped: 0, tooOld: 0 }),
    // Le disco e le demo storiche di Close (22/10/2026): appuntamenti già chiusi nati, aggiornati, invariati, rimasti fuori; `ambiguous` le
    // riunioni che non si capisce se siano una disco o una demo, rimaste soltanto nella Timeline.
    appointments: z
        .object({ create: countSchema, update: countSchema, unchanged: countSchema, skipped: countSchema, ambiguous: countSchema })
        .default({ create: 0, update: 0, unchanged: 0, skipped: 0, ambiguous: 0 }),
    // Le ricerche salvate di Close tradotte in Smart List (22/10/2026): nate, aggiornate, invariate, rimaste fuori (non traducibili, senza
    // owner, nome già preso). `staged.savedSearches` resta quante ce ne sono in Close.
    savedSearches: z.object({ create: countSchema, update: countSchema, unchanged: countSchema, skipped: countSchema }).default({ create: 0, update: 0, unchanged: 0, skipped: 0 }),
    staged: z.object({ opportunities: countSchema.optional(), tasks: countSchema.optional(), savedSearches: countSchema }),
    notImported: z.record(z.string(), countSchema),
    anomalies: z.record(z.string(), countSchema),
    users: z.array(importUserLinkSchema),
    /** Per utente di Close: Lead di cui è owner in Close, chiamate e note di cui è autore (soltanto conteggi). */
    userCounts: z.array(z.object({ closeUserId: z.string(), leadsOwned: countSchema, calls: countSchema, notes: countSchema })).default([]),
    /** I Lead di Close senza owner in Close. */
    leadsWithoutOwner: countSchema.default(0),
    /**
     * Le persone di Close che non sono nel team (INGR-15): `create` nate sospese nell'esecuzione (nella prova a secco: che
     * nascerebbero), `existing` nate prima, `historic` autori storici, `invited` con un invito in attesa.
     */
    closePeople: z.object({ create: countSchema, existing: countSchema, historic: countSchema, invited: countSchema }).default({ create: 0, existing: 0, historic: 0, invited: 0 }),
    variables: z.array(importVariableSchema),
});
/**
 * Lo stato del rapporto di un'esecuzione:
 * - `ready`: il riepilogo salvato si legge (anche uno salvato prima dei campi più recenti, con i loro valori di partenza);
 * - `none`: l'esecuzione non ha salvato un riepilogo perché non è arrivata alla fine: è in corso, oppure si è fermata
 *   (`run.status` dice quale dei due);
 * - `unreadable`: un riepilogo c'è, o dovrebbe esserci perché l'esecuzione è finita, ma non ha la forma del contratto.
 */
export const IMPORT_SUMMARY_STATUSES = ["ready", "none", "unreadable"];
/**
 * `GET /api/imports/close/runs/:runId`. `summary` c'è soltanto con `summaryStatus` `ready`. `progress` sono i record letti
 * da Close per risorsa, salvati dall'estrazione insieme al suo checkpoint: per un'esecuzione in corso il punto a cui è
 * arrivata; nullo prima della prima pagina.
 */
export const importRunDetailSchema = z.object({
    run: importRunRowSchema,
    summaryStatus: z.enum(IMPORT_SUMMARY_STATUSES),
    summary: importSummarySchema.nullable(),
    progress: z.record(z.string(), z.object({ fetched: z.number().int() })).nullable(),
});
/**
 * I codici delle anomalie dell'importazione di Close (piano M2, Task 11): ciò che l'estrazione e il mapping trovano e che
 * il rapporto conta e mostra. Una lista sola per l'importatore (che la riesporta come `ANOMALY_CODES`) e per la pagina
 * «Importazione da Close», che dà a ognuno un livello e i suoi testi: un codice nuovo senza livello non passa il
 * typecheck dell'interfaccia. Nelle risposte dell'API `code` resta una stringa: un rapporto salvato con un codice che
 * l'interfaccia non conosce ancora si legge lo stesso.
 */
export const IMPORT_ANOMALY_CODES = [
    "no_phone",
    "phone_from_calls",
    "invalid_phone",
    "duplicate_phone",
    // Un Lead di Close mai importato con il cellulare di un solo Lead di Gioia365 che non viene da Close: si unisce a quello (INGR-14).
    "joined_by_mobile",
    "extra_contacts",
    "extra_phones",
    "city_not_found",
    "city_ambiguous",
    "city_foreign",
    "unknown_status",
    "unmapped_option",
    "field_conflict",
    "owner_not_linked",
    "author_not_linked",
    "name_split_uncertain",
    "created_on_used",
    "last_call_mismatch",
    "missing_in_source",
    "local_change_kept",
    "source_changed_after_import",
    "activity_not_imported",
    "recording_not_copied",
    // Un record di Close con caratteri NUL, che PostgreSQL non ammette: tolti prima di salvarlo (estrazione, Task 12).
    "nul_characters_removed",
    // Una data di Close che non è un giorno vero fra gli anni 0001 e 9999 (mapping, Task 13): non si scrive; vale la
    // data successiva nell'ordine della regola, o l'attività non si importa.
    "invalid_date",
    // Un'email di un titolare che la regola del prodotto non accetta: non diventa l'email, resta in una nota (Task 13).
    "invalid_email",
    // Un Lead senza altri numeri che prende per il titolare il primo numero valido di un altro contatto: il fisso del
    // secondo titolare o un contatto dal terzo (Task 13).
    "phone_from_other_contact",
    // Gli allegati di una nota di Close, che restano in Close: il dettaglio dice quanti (Task 13).
    "attachments_not_copied",
    // Un valore di Close più lungo di quanto il prodotto ammette per quel campo del Lead (per esempio un nome oltre i 120
    // caratteri): il campo resta vuoto e il valore intero va in una nota (importazione, Task 14). Il dettaglio dice il
    // campo, la lunghezza e il limite.
    "value_too_long",
    // Un Lead già importato che in Gioia365 è archiviato o unito a un altro: l'importazione non ne aggiorna la scheda (Task 14); la sua
    // storia nuova entra lo stesso, sul Lead che resta se è unito, Task e opportunità nuovi in una nota (23/10/2026). Il dettaglio dice il
    // motivo (`archived`, `merged`, `missing`: un Lead eliminato per sempre, che non riceve più niente).
    "lead_not_updatable",
    // Le opportunità di Close (21/10/2026, INGR-26): una fase che il profilo non conosce (`closeStatus`, `statusType`), che non
    // diventa un'opportunità: i suoi dati vanno in una nota sulla scheda del Lead.
    "unknown_opportunity_status",
    // Un'opportunità di Close che non entra e va in una nota sulla scheda: il motivo (`open_exists`: il Lead ne ha già una
    // aperta; `no_assignee`: nessuna persona del team la può seguire; `lead_not_updatable`: il Lead è archiviato o unito).
    "opportunity_not_imported",
    // Un'opportunità che entra senza un dato: `value_missing` (venduta senza valore), `value_invalid` (un valore che Gioia365 non
    // ammette), `closer_not_linked` (venduta da chi non è una persona dell'azienda: non conta nei report delle vendite).
    "opportunity_incomplete",
    // Un Task aperto di Close che non diventa un richiamo e va in una nota sulla scheda (22/10/2026): il motivo (`open_exists`: il Lead
    // ha già un richiamo aperto; `no_assignee`: nessuna persona attiva del team lo può avere; `unknown_type`: un tipo di Task di Close che
    // non è una telefonata, con `closeType`; `too_old`: scaduto da più di 30 giorni; `lead_not_updatable`: il Lead è archiviato o unito,
    // e la nota va sul Lead che ne riceve la storia, 23/10/2026).
    "task_not_imported",
    // Una ricerca salvata di Close che non diventa una Smart List (22/10/2026): il motivo (`CLOSE_SAVED_SEARCH_REASONS`), mai i filtri.
    "saved_search_not_imported",
    // Una disco o una demo storica di Close che non diventa un appuntamento (22/10/2026): `duplicate` (lo stesso appuntamento c'è già in
    // Gioia365), `no_person` (né chi l'ha prenotata né l'owner del Lead sono persone dell'azienda), `ambiguous` (una riunione di Close
    // che non si capisce se sia una disco o una demo: resta nella Timeline e non conta), con il tipo quando si sa.
    "appointment_not_imported",
];
/**
 * I tipi di attività di Close (`_type`) che Gioia365 conosce e non importa: il riepilogo di una conversazione email
 * (`EmailThread`, le sue email entrano una a una come `Email`, 22/10/2026) e gli SMS, che non fanno parte del prodotto. Una
 * lista sola per l'importatore, che li conta come non importati con il motivo `email_sms_out_of_scope`, e per la pagina
 * «Importazione da Close», che li mostra a parte e mai fra i tipi che Gioia365 non conosce.
 */
export const CLOSE_OUT_OF_SCOPE_ACTIVITY_TYPES = ["EmailThread", "SMS"];
/** Le anomalie di una pagina di «Da controllare»: di partenza dieci alla volta, al massimo cento. */
export const IMPORT_ANOMALY_PAGE_DEFAULT = 10;
export const IMPORT_ANOMALY_PAGE_MAX = 100;
/** Query di `GET /api/imports/close/runs/:runId/anomalies`: un parametro sconosciuto o fuori dai limiti è `422`. */
export const importAnomalyQuerySchema = z.strictObject({
    code: z
        .string()
        .regex(/^[a-z][a-z_]{0,63}$/)
        .optional(),
    offset: listOffsetSchema,
    limit: z.coerce.number().int().min(1).max(IMPORT_ANOMALY_PAGE_MAX).default(IMPORT_ANOMALY_PAGE_DEFAULT),
});
/**
 * Una pagina di anomalie: il riferimento di Close (risorsa e identificativo), il codice, il dettaglio (soltanto valori
 * di campi a scelta, conteggi e riferimenti tecnici) e il Lead di Gioia365 quando esiste e chi guarda lo vede, per
 * «Apri il Lead». `total` conta le righe, non i casi: `author_not_linked` è una riga per utente di Close.
 */
export const importAnomalyPageSchema = z.object({
    items: z.array(z.object({
        resource: z.string(),
        externalId: z.string(),
        code: z.string(),
        severity: z.enum(["info", "warning", "error"]),
        detail: z.record(z.string(), z.union([z.string(), z.number(), z.boolean(), z.null()])),
        leadId: z.string().nullable(),
    })),
    total: z.number().int(),
});
// ---------------------------------------------------------------------------------------------------------------
// L'importazione da Close di ogni sera (INGR-12, confermata da Lucio il 18/10/2026): la sezione «Ogni sera da Close» della pagina.
// La chiave API di Close entra soltanto con «Salva la chiave» e non esce mai: la risposta dice soltanto quando e chi l'ha salvata.
// Le date sono stringhe ISO; il giorno della sera è il giorno di Roma (`AAAA-MM-GG`).
/** La chiave API di Close come la si incolla: caratteri stampabili senza spazi, al più 200. */
export const closeApiKeySchema = z
    .string()
    .trim()
    .min(1)
    .max(200)
    .regex(/^[\x21-\x7e]+$/);
/** `PUT /api/imports/close/nightly/key`: la chiave, provata con Close prima di salvarla. */
export const saveCloseKeyBodySchema = z.object({ apiKey: closeApiKeySchema });
/** `PUT /api/imports/close/nightly/schedule`: «Importa ogni sera alle 21:00» acceso o spento. */
export const closeNightlyScheduleBodySchema = z.object({ enabled: z.boolean() });
/** Com'è andata l'ultima importazione della sera: in corso, finita, fermata dallo spegnimento del server, fermata con errore. */
export const CLOSE_NIGHTLY_OUTCOMES = ["running", "completed", "interrupted", "failed"];
/**
 * `GET /api/imports/close/nightly`. Chi ha `import_leads` legge interruttore, prossima importazione e ultima della sera; soltanto il
 * Super admin di Gioia365 (`canManage`) legge anche chiave e prove a secco e può cambiare. `dryRun` è l'ultima prova a secco avviata
 * dalla pagina (`running` senza `busy` vuol dire fermata); `dryRunCompletedAt` quando è finita l'ultima con la chiave di adesso. `ready`:
 * c'è la chiave e una prova a secco finita con la chiave di adesso, quindi «Importa adesso» e l'interruttore si possono usare; `busy`:
 * un'importazione da Close è in corso e viva. `next.day`: `soon` dopo le 21:00, finché quella di stasera non è partita.
 */
export const closeNightlyStatusSchema = z.object({
    canManage: z.boolean(),
    key: z.object({ savedAt: z.string(), savedBy: z.string().nullable() }).nullable(),
    dryRun: z
        .object({
        runId: z.string(),
        status: importRunRowSchema.shape.status.nullable(),
        finishedAt: z.string().nullable(),
        currentKey: z.boolean(),
    })
        .nullable(),
    dryRunCompletedAt: z.string().nullable(),
    ready: z.boolean(),
    busy: z.boolean(),
    nightly: z.object({
        enabled: z.boolean(),
        next: z.object({ at: z.string(), day: z.enum(["soon", "today", "tomorrow"]) }).nullable(),
        last: z
            .object({
            day: z.string(),
            runId: z.string().nullable(),
            outcome: z.enum(CLOSE_NIGHTLY_OUTCOMES),
            error: z.string().nullable(),
            startedAt: z.string().nullable(),
            finishedAt: z.string().nullable(),
        })
            .nullable(),
    }),
});
/** `POST …/nightly/dry-run` e `POST …/nightly/import`: l'esecuzione partita (o ripresa), che prosegue nel server. */
export const closeRunStartedSchema = z.object({ runId: z.string(), status: closeNightlyStatusSchema });
// ---------------------------------------------------------------------------------------------------------------
// «Pronti a spegnere Close?» (M8, la verifica del passaggio, 22/10/2026): soltanto il Super admin di Gioia365. Dall'ultima importazione
// vera finita, risorsa per risorsa, quanti oggetti ci sono in Close e quanti ne sono entrati in Gioia365, con la differenza spiegata;
// lo stato della copia dell'audio e degli allegati delle note (INGR-27) e, in cima, un giudizio: niente da fare prima della disdetta, oppure che cosa manca. Tutto
// viene dai dati che l'importazione ha già salvato (record, collegamenti, anomalie, riepilogo): nessuna chiamata a Close. Soltanto
// conteggi e codici: mai testo libero né dati personali (INGR-17).
/**
 * Le righe della verifica, in quest'ordine. I Task aperti entrano come richiami (22/10/2026); le ricerche salvate sono soltanto contate:
 * non entrano in Gioia365, e la verifica ne dà i nomi (`savedSearchNames`). Gli allegati delle note non sono una riga: come l'audio,
 * hanno lo stato della loro copia (`closeHandoverAttachmentsSchema`).
 */
export const CLOSE_HANDOVER_RESOURCES = ["leads", "calls", "notes", "emails", "events", "opportunities", "otherActivities", "openTasks", "savedSearches", "people"];
/**
 * Perché un oggetto di Close non è (o non è da solo) in Gioia365, in quest'ordine:
 * - `no_phone`: Lead senza un numero valido, che non entra (INGR-14);
 * - `merged`: Lead entrato e poi unito a un altro con «Pulizia dati»: la sua storia è sul Lead che resta;
 * - `lead_not_imported`: attività od opportunità di un Lead che non è entrato;
 * - `lead_not_updatable`: di un Lead eliminato per sempre, o archiviato o unito mentre l'importazione girava (la prossima riprova): la
 *   storia di un Lead archiviato o unito entra lo stesso dal 23/10/2026;
 * - `invalid_date`: attività senza una data valida;
 * - `in_note`: opportunità o Task aperto entrato come nota sulla scheda (INGR-26);
 * - `too_old`: Task aperto scaduto da più di 30 giorni, entrato come nota e non come richiamo (INGR-26);
 * - `out_of_scope`: riepiloghi delle conversazioni email (le loro email entrano una a una) e SMS, fuori dal prodotto;
 * - `unknown_type`: attività di un tipo che Gioia365 non conosce;
 * - `new_after_run`: arrivato in Close dopo l'ultima importazione finita: entra con la prossima;
 * - `counted_only`: ricerche salvate che non sono diventate Smart List (il motivo in `savedSearchesLeft`) e che nessuno ha ancora deciso;
 * - `decided`: ricerche salvate che non sono diventate Smart List, decise nella verifica: «Rifatta a mano» o «Lasciata perdere» (23/10/2026);
 * - `historic_author`: persona di Close che resta soltanto come autore delle sue attività (senza email, gestore della piattaforma,
 *   azienda senza il ruolo User);
 * - `invited`: persona di Close invitata nel team che non ha ancora accettato.
 */
export const CLOSE_HANDOVER_REASONS = [
    "no_phone",
    "merged",
    "lead_not_imported",
    "lead_not_updatable",
    "invalid_date",
    "in_note",
    "too_old",
    "out_of_scope",
    "unknown_type",
    "new_after_run",
    "counted_only",
    "decided",
    "historic_author",
    "invited",
];
/**
 * Una riga: `close` gli oggetti in Close, `gioia365` quanti di loro sono in Gioia365 (`null`: ancora soltanto contati), `explained`
 * le differenze col motivo, `unexplained` quelle senza: `close = gioia365 + Σ explained + unexplained`.
 */
export const closeHandoverRowSchema = z.object({
    resource: z.enum(CLOSE_HANDOVER_RESOURCES),
    close: countSchema,
    gioia365: countSchema.nullable(),
    explained: z.array(z.object({ reason: z.enum(CLOSE_HANDOVER_REASONS), count: countSchema })),
    unexplained: countSchema,
});
/**
 * L'audio delle chiamate di Close (INGR-27): `withAudio` le chiamate entrate che in Close hanno un audio; di queste `copied` copiate,
 * `toCopy` ancora da copiare (in coda o non ancora messe in coda), `failed` non riuscite dopo tutti i tentativi, `missing` che Close
 * non ha più. `copyOn`: la copia è accesa (c'è l'archivio dell'audio, le variabili `STORAGE_*`).
 */
export const closeHandoverAudioSchema = z.object({
    copyOn: z.boolean(),
    withAudio: countSchema,
    copied: countSchema,
    toCopy: countSchema,
    failed: countSchema,
    missing: countSchema,
});
/**
 * Gli allegati delle note di Close (INGR-27, come l'audio): `total` quelli delle note entrate; di questi `copied` copiati, `toCopy`
 * ancora da copiare (in coda o non ancora messi in coda), `failed` non riusciti (dopo tutti i tentativi, o oltre il limite di misura),
 * `missing` che Close non ha più. `copyOn`: la copia è accesa (lo stesso archivio dell'audio).
 */
export const closeHandoverAttachmentsSchema = z.object({
    copyOn: z.boolean(),
    total: countSchema,
    copied: countSchema,
    toCopy: countSchema,
    failed: countSchema,
    missing: countSchema,
});
/** Dopo quante ore l'ultima importazione finita non vale più come quella di oggi: la sera dopo è già passata. */
export const CLOSE_HANDOVER_FRESH_HOURS = 26;
/**
 * Che cosa manca prima della disdetta, in quest'ordine (il più importante per primo). Il controllo vede soltanto ciò che Gioia365 ha
 * letto da Close: vale dopo che Close è in sola lettura e dopo l'ultima importazione.
 * - `no_import`: nessuna importazione vera è finita;
 * - `import_old`: l'ultima è finita da più di `CLOSE_HANDOVER_FRESH_HOURS` ore;
 * - `unexplained`: una differenza senza motivo in una risorsa;
 * - `new_after_run`: oggetti arrivati in Close dopo l'ultima importazione;
 * - `audio_copy_off`: audio da copiare con la copia spenta; `audio_to_copy`: audio ancora da copiare; `audio_failed`: copie non riuscite;
 * - `attachments_copy_off`: allegati da copiare con la copia spenta; `attachments_to_copy`: allegati ancora da copiare;
 *   `attachments_failed`: copie non riuscite, da scaricare a mano da Close prima della disdetta;
 * - da decidere (Lucio), perché con la disdetta si perdono: `lead_not_updatable` attività od opportunità di un Lead eliminato per
 *   sempre (o in corsa con l'importazione); `unknown_type` attività di tipi che Gioia365 non conosce; `counted_only` ricerche salvate di
 *   Close non ancora decise («Rifatta a mano» o «Lasciata perdere»).
 */
export const CLOSE_HANDOVER_ITEMS = [
    "no_import",
    "import_old",
    "unexplained",
    "new_after_run",
    "audio_copy_off",
    "audio_to_copy",
    "audio_failed",
    "attachments_copy_off",
    "attachments_to_copy",
    "attachments_failed",
    "lead_not_updatable",
    "unknown_type",
    "counted_only",
];
/**
 * Perché una ricerca salvata di Close non diventa una Smart List (22/10/2026): `not_lead` non cerca Lead; `text_query` è una ricerca a
 * testo; `user_dependent` dipende da chi la guarda («i miei Lead», SL-7); `nesting` ha più livelli di quanti una Smart List ne tenga;
 * `unknown_clause` ha una clausola che le Smart List non hanno (date, attività collegate, testo); `field` cerca un campo che in Gioia365
 * è una nota; `status`, `owner`, `tag`, `value`: uno status, un owner, un tag o una voce che in Gioia365 non c'è; `too_long` diventa un
 * filtro troppo lungo; `no_owner` è di chi non è una persona attiva dell'azienda; `name_taken` il suo owner ha già una lista con quel
 * nome (anche con «(Close)»); `not_examined` non ancora esaminata.
 */
export const CLOSE_SAVED_SEARCH_REASONS = [
    "not_lead",
    "text_query",
    "user_dependent",
    "nesting",
    "unknown_clause",
    "field",
    "status",
    "owner",
    "tag",
    "value",
    "too_long",
    "no_owner",
    "name_taken",
    "not_examined",
];
/**
 * Che cosa si è deciso di una ricerca salvata di Close che non è diventata una Smart List (23/10/2026, INGR-28): `rebuilt` «Rifatta a
 * mano», `dropped` «Lasciata perdere». Decide il solo Super admin di Gioia365, nella verifica; si può annullare.
 */
export const CLOSE_SAVED_SEARCH_DECISIONS = ["rebuilt", "dropped"];
/** `PUT /api/imports/close/handover/saved-searches/:id`: la decisione su una ricerca salvata; `DELETE` la annulla. */
export const closeSavedSearchDecisionBodySchema = z.object({ decision: z.enum(CLOSE_SAVED_SEARCH_DECISIONS) });
/** Quanti nomi di ricerche salvate di Close la verifica mostra al più. */
export const CLOSE_HANDOVER_SAVED_SEARCH_NAMES_MAX = 200;
export const closeHandoverItemSchema = z.object({ code: z.enum(CLOSE_HANDOVER_ITEMS), resource: z.enum(CLOSE_HANDOVER_RESOURCES).nullable(), count: countSchema });
/**
 * `GET /api/imports/close/handover`: `run` l'ultima importazione vera finita (nulla se non ce n'è), le righe (vuote senza), l'audio,
 * gli allegati, i nomi delle ricerche salvate di Close (in ordine alfabetico, al più `CLOSE_HANDOVER_SAVED_SEARCH_NAMES_MAX`: soltanto
 * i nomi, per rifarle a mano come Smart List; mai i loro filtri) e il giudizio: `ready` se `items` è vuoto («Tutto a posto»).
 */
export const closeHandoverSchema = z.object({
    run: z.object({ id: z.string(), startedAt: z.string(), finishedAt: z.string() }).nullable(),
    rows: z.array(closeHandoverRowSchema),
    audio: closeHandoverAudioSchema,
    attachments: closeHandoverAttachmentsSchema,
    savedSearchNames: z.array(z.string()).default([]),
    /**
     * Le ricerche salvate che non sono diventate Smart List (22/10/2026), con il motivo: i nomi di `savedSearchNames`, nello stesso ordine
     * (`""` per una senza nome). `id` è l'identificativo di Close, per decidere; `decision` che cosa si è deciso, da chi e quando (23/10/2026).
     */
    savedSearchesLeft: z
        .array(z.object({
        id: z.string(),
        name: z.string(),
        reason: z.enum(CLOSE_SAVED_SEARCH_REASONS),
        decision: z.object({ kind: z.enum(CLOSE_SAVED_SEARCH_DECISIONS), by: z.string(), at: z.string() }).nullable(),
    }))
        .default([]),
    verdict: z.object({ ready: z.boolean(), items: z.array(closeHandoverItemSchema) }),
});
// ---------------------------------------------------------------------------------------------------------------
// La lettura da GoHighLevel (INGR-29, scelta di Claude 24/10/2026 su richiesta di Lucio): Impostazioni → «Importazione ed
// esportazione» → «GoHighLevel». In questo passo soltanto il posto della chiave (Private Integration Token del sotto-account, con il
// suo Location ID) e la prova di lettura: niente entra in Gioia365 (INGR-2). Il token entra soltanto con «Salva la chiave» e non esce
// mai; la prova di lettura dice soltanto numeri ed esiti, mai nomi o dati dei contatti.
/** Il Private Integration Token come lo si incolla: caratteri stampabili senza spazi, al più 500. */
export const ghlPrivateTokenSchema = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^[\x21-\x7e]+$/);
/** L'ID della posizione (Location ID) del sotto-account di GoHighLevel: lettere, cifre, `-` e `_`, al più 64. */
export const ghlLocationIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/);
/** `PUT /api/imports/ghl/key`: il token e la posizione, provati con GoHighLevel prima di salvarli. */
export const saveGhlKeyBodySchema = z.object({ token: ghlPrivateTokenSchema, locationId: ghlLocationIdSchema });
/**
 * L'esito di una lettura della prova: letta (`ok`), chiave non riconosciuta (401), permessi mancanti o posizione d'altri (403), troppe
 * richieste (429), GoHighLevel che non risponde o risponde in modo inatteso (`unavailable`).
 */
export const GHL_READ_OUTCOMES = ["ok", "rejected", "forbidden", "rate_limited", "unavailable"];
/** Una risorsa letta dalla prova: l'esito e, se letta, quanti ce ne sono (mai che cosa). */
export const ghlProbeCountSchema = z.object({ outcome: z.enum(GHL_READ_OUTCOMES), count: z.number().int().nonnegative().nullable() });
/** L'ultima «Prova la lettura»: quando, chi, e per contatti e conversazioni l'esito con il numero. */
export const ghlProbeSchema = z.object({
    at: z.string(),
    by: z.string().nullable(),
    contacts: ghlProbeCountSchema,
    conversations: ghlProbeCountSchema,
});
/**
 * `GET /api/imports/ghl` (e la risposta delle scritture). Chi ha `import_leads` legge se la chiave c'è e l'ultima prova di lettura;
 * il Super admin di Gioia365 e il Super admin dell'azienda (`canManage`, INGR-29) anche quando e da chi è stata salvata e la posizione,
 * e la cambiano, la tolgono e la provano.
 */
export const ghlReadStatusSchema = z.object({
    canManage: z.boolean(),
    connected: z.boolean(),
    key: z.object({ savedAt: z.string(), savedBy: z.string().nullable(), locationId: z.string() }).nullable(),
    lastProbe: ghlProbeSchema.nullable(),
    /** L'importazione (INGR-30): soltanto per il Super admin di Gioia365, altrimenti nulla. */
    importer: z.lazy(() => ghlImporterStatusSchema).nullable(),
});
// ---------------------------------------------------------------------------------------------------------------
// L'importazione da GoHighLevel (INGR-30, decisioni di Lucio del 24/10/2026): «Prova senza salvare» legge tutto in sola lettura e
// dice soltanto numeri e i nomi dei tag (INGR-17: niente testo libero né dati personali); il Super admin di Gioia365 conferma i tag
// voce per voce (INGR-2); «Importa adesso» scrive, ritentabile e senza doppioni.
/** Il destino di un tag di GoHighLevel: entra (una campagna), tecnico (non entra), delle assunzioni (non entra). */
export const GHL_IMPORT_TAG_DESTINIES = ["enter", "technical", "candidates"];
/** I canali dei messaggi che entrano come storia (PROD-3: mai inviati di nuovo). */
export const GHL_IMPORT_CHANNELS = ["email", "whatsapp", "instagram", "facebook", "sms", "call"];
const ghlCount = z.number().int().nonnegative();
export const ghlImportReportSchema = z.object({
    contacts: z.object({
        read: ghlCount,
        /** Incrociati con un Lead, per la chiave che l'ha trovato (INGR-22: contatto già noto, email, telefono). */
        matched: z.object({ byContact: ghlCount, byEmail: ghlCount, byPhone: ghlCount }),
        /** Degli incrociati, quelli con un Lead archiviato: ricevono la storia, non i tag (INGR-28). */
        matchedArchived: ghlCount,
        /** Soltanto in GoHighLevel e con le condizioni dei Lead nuovi: nella prova da creare, nell'importazione creati o già creati. */
        toCreate: ghlCount,
        /** Soltanto in GoHighLevel, con il cellulare o l'email di un altro contatto che diventa un Lead nuovo: lo raggiungono, non ne fanno un altro. */
        sameNewLead: ghlCount,
        candidates: z.object({ total: ghlCount, alreadyLeads: ghlCount }),
        uncertain: z.object({ total: ghlCount, matched: ghlCount, notCreated: ghlCount }),
        discarded: z.object({ noItalianMobile: ghlCount, notQualified: ghlCount, notHairdresser: ghlCount }),
    }),
    owner: z.object({ name: z.string(), found: z.boolean() }),
    hiringPipeline: z.object({ readable: z.boolean(), pipelines: ghlCount, contacts: ghlCount }),
    conversations: z.object({ read: ghlCount, outOfScope: ghlCount }),
    /** Quanti contatti e conversazioni GoHighLevel dice di avere: diversi da quelli letti, la lettura è monca. */
    listed: z.object({ contacts: ghlCount.nullable(), conversations: ghlCount.nullable() }),
    messages: z.array(z.object({ channel: z.enum(GHL_IMPORT_CHANNELS), single: ghlCount, counted: ghlCount })),
    skippedMessages: z.array(z.object({ kind: z.string(), count: ghlCount })),
    tags: z.array(z.object({ name: z.string(), contacts: ghlCount, destiny: z.enum(GHL_IMPORT_TAG_DESTINIES) })),
    written: z
        .object({
        leadsCreated: ghlCount,
        leadsAlreadyCreated: ghlCount,
        tagsAdded: ghlCount,
        messages: ghlCount,
        summaries: ghlCount,
        alreadyImported: ghlCount,
        smartList: z.enum(["created", "existing", "missing"]),
    })
        .nullable(),
    requests: ghlCount,
});
/**
 * Quanto la lettura di una prova resta sotto quello che GoHighLevel dichiara, per contatti e conversazioni. Fino allo 0,1% di ogni voce la
 * differenza è tollerata e si dice (di solito voci cancellate o doppie nel suo elenco); oltre, «Importa adesso» non parte (scelta di Claude
 * 24/10/2026, dopo la prima prova vera: 9.288 conversazioni lette su 9.290 dichiarate).
 */
export function ghlReadingGap(report) {
    const conversationsRead = report.conversations.read + report.conversations.outOfScope;
    const contacts = report.listed.contacts === null ? 0 : Math.max(0, report.listed.contacts - report.contacts.read);
    const conversations = report.listed.conversations === null ? 0 : Math.max(0, report.listed.conversations - conversationsRead);
    const tolerated = (gap, listed) => gap === 0 || (listed !== null && gap <= Math.floor(listed / 1000));
    return { contacts, conversations, complete: tolerated(contacts, report.listed.contacts) && tolerated(conversations, report.listed.conversations) };
}
export const GHL_IMPORT_RUN_STATUSES = ["running", "completed", "failed", "aborted"];
/** Un'esecuzione: quando, com'è andata (con il solo codice dell'errore) e il rapporto, quando c'è. */
export const ghlImportRunSchema = z.object({
    runId: z.string(),
    status: z.enum(GHL_IMPORT_RUN_STATUSES),
    startedAt: z.string(),
    finishedAt: z.string().nullable(),
    error: z.string().nullable(),
    report: ghlImportReportSchema.nullable(),
});
export const ghlImporterStatusSchema = z.object({
    /** Una prova o un'importazione in corso. */
    busy: z.boolean(),
    dryRun: ghlImportRunSchema.nullable(),
    /** L'ultima prova a secco è partita con la chiave di adesso. */
    dryRunCurrent: z.boolean(),
    importRun: ghlImportRunSchema.nullable(),
    /** I tag confermati, per quale prova a secco. */
    tagChoices: z.object({ runId: z.string(), at: z.string(), by: z.string().nullable(), entering: z.array(z.string()) }).nullable(),
    /** «Importa adesso» si può premere: prova a secco finita con questa chiave e tag confermati per lei. */
    ready: z.boolean(),
    /** Gira l'importazione da Close (o sono fra le 21:00 e le 22:05 di Roma): prova e importazione non partono. */
    closeBusy: z.boolean(),
    /** Dove vanno i Lead nuovi, dal profilo del server: la persona owner, il tag, la Smart List. */
    newLeads: z.object({ owner: z.string(), tag: z.string(), smartList: z.string() }),
});
/** `PUT /api/imports/ghl/tags`: i tag che entrano, confermati per la prova a secco `runId`. */
export const saveGhlTagChoicesBodySchema = z.object({
    runId: z.string().min(1).max(100),
    entering: z.array(z.string().min(1).max(300)).max(5_000),
});
// ---------------------------------------------------------------------------------------------------------------
// La scrittura su GoHighLevel (INGR-19; campi e tag: scelta di Claude 29/10/2026 su delega di Lucio): Gioia365 tiene aggiornati i
// contatti di GoHighLevel perché le automazioni delle email li usino. Soltanto il Super admin di Gioia365 la vede e la cambia:
// l'interruttore (spento di partenza), «Crea i contatti che mancano» (spento di partenza), la «Prova senza scrivere» con qualche
// esempio mascherato, l'ultimo giro e la coda. Mai un dato intero di una persona.
/** Gli ambiti che il token deve avere per scrivere, oltre a quelli di lettura: la pagina li dice quando GoHighLevel rifiuta. */
export const GHL_WRITE_SCOPES = ["contacts.write", "locations/customFields.write"];
/** I conteggi di una prova o di un giro: considerati, da aggiornare, da creare, invariati, senza contatto, con più contatti, rifiutati da GoHighLevel, archiviati, falliti. */
export const ghlWriteCountsSchema = z.object({
    considered: ghlCount,
    update: ghlCount,
    create: ghlCount,
    unchanged: ghlCount,
    noMatch: ghlCount,
    ambiguous: ghlCount,
    rejected: ghlCount,
    archived: ghlCount,
    failed: ghlCount,
});
/** Un esempio della prova: nome, email e telefono mascherati; i campi «G365 · …» che si scriverebbero, con il loro valore. */
export const ghlWriteExampleSchema = z.object({
    operation: z.enum(["update", "create", "skip"]),
    matchedBy: z.enum(["contact", "email", "phone"]).nullable(),
    reason: z.string().nullable(),
    name: z.string(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    fields: z.record(z.string(), z.string()),
    tags: z.array(z.string()),
});
export const GHL_WRITE_OUTCOMES = ["ok", "forbidden", "fields_missing", "rate_limited", "unavailable", "close_window"];
export const ghlWriterStatusSchema = z.object({
    /** C'è la chiave (senza, niente si accende). */
    connected: z.boolean(),
    enabled: z.boolean(),
    createMissing: z.boolean(),
    changed: z.object({ at: z.string(), by: z.string().nullable() }).nullable(),
    /** Perché la scrittura si è spenta da sola. */
    stoppedReason: z.enum(["forbidden", "fields_missing"]).nullable(),
    /** Gli ambiti di scrittura che il token deve avere. */
    requiredScopes: z.array(z.string()),
    /** I nomi dei campi «G365 · …» e dei tag che Gioia365 scrive. */
    fields: z.array(z.string()),
    tags: z.array(z.string()),
    lastDryRun: z
        .object({
        at: z.string(),
        by: z.string().nullable(),
        /** Fatta con la chiave di adesso: soltanto allora l'interruttore si accende. */
        current: z.boolean(),
        outcome: z.enum(GHL_WRITE_OUTCOMES),
        counts: ghlWriteCountsSchema,
        missingFields: z.array(z.string()),
        examples: z.array(ghlWriteExampleSchema),
    })
        .nullable(),
    lastRound: z.object({ at: z.string(), outcome: z.enum(GHL_WRITE_OUTCOMES), counts: ghlWriteCountsSchema }).nullable(),
    queue: z.object({ pending: ghlCount, done: ghlCount, skipped: ghlCount, failed: ghlCount }),
});
/** `PUT /api/imports/ghl/write`: l'interruttore e «Crea i contatti che mancano». */
export const saveGhlWriteSettingsBodySchema = z.object({ enabled: z.boolean(), createMissing: z.boolean() });
/** `POST /api/imports/ghl/write/dry-run`: quanti Lead provare (i più recenti), da 1 a 50; di partenza 20. */
export const ghlWriteDryRunBodySchema = z.object({ sample: z.number().int().min(1).max(50).default(20) });
//# sourceMappingURL=imports.js.map